import React from "react";

import { DATASHEET_MIN_FIELD_SIZE } from "@modules/datasheets/components/editor/editor.constants";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";
import { FieldType } from "@common/dto/field/type";
import { getTableFieldSubfields } from "@common/actions/formats";

import { IDatasheetEditorElement } from "../../datasheet.types";
import { useDatasheetElementController } from "../../hooks/datasheet-element.controller";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetFieldComponentOriginFieldGetDto } from "@common/dto/datasheet/field-component/origin-field-dto";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";
import { getTableReportFormats } from "@modules/datasheets/services/datasheet";

import { DatasheetElement } from "@modules/datasheets/types/datasheet-element";

export type TableFieldProps = IDatasheetEditorElement<DatasheetFieldComponentGetDto> & {
  onTabOrderChange: (datasheetElement: DatasheetElement<DatasheetFieldComponentGetDto>) => void;
  parentFormatId?: number;
};

export const shouldHydrateTableSubfields = (original: DatasheetFieldComponentGetDto): boolean => {
  const fieldTypeId = original.field.fieldtype?.typeid ?? original.field.type;
  if (fieldTypeId !== FieldType.table) {
    return false;
  }

  const tableReportFormat = (original as { tableReportFormat?: string | null }).tableReportFormat;
  if (tableReportFormat != null) {
    return false;
  }

  const hasExplicitDsSubfields = Object.prototype.hasOwnProperty.call(original, "dssubfields");
  if (hasExplicitDsSubfields && Array.isArray(original.dssubfields)) {
    return false;
  }

  const hasLegacyDsSubfields = (original.m_ds_subfields?.length ?? 0) > 0;
  if (hasLegacyDsSubfields) {
    return false;
  }

  return true;
};

export const useTableFieldController = (props: TableFieldProps) => {
  const {
    datasheetElement,
    containerRef,
    isSelected,
    onSelect,
    onCoordinatesChange,
    datasheetGridSettings,
    onResize,
    multipleElementsSelected,
    resizeHandlerStrategy,
    isTabOrderSettingActive,
    onTabOrderChange,
    parentFormatId,
  } = props;

  // Column-resize calls always include `original` in the payload, so those pass straight through.
  // Using `data: any` so the callback is assignable to onResize's generic signature.
  const handleOuterResize = React.useCallback(
    (id: string, data: any) => {
      if ('original' in data || data.width == null) {
        onResize(id, data);
        return;
      }

      const dsSubfields =
        datasheetElement.original.dssubfields ||
        datasheetElement.original.m_ds_subfields ||
        [];

      if (dsSubfields.length === 0) {
        onResize(id, data);
        return;
      }

      const allButLast = dsSubfields.slice(0, -1);
      const totalOtherWidth = allButLast.reduce(
        (sum: number, sf: DatasheetFieldComponentGetDto) => sum + (sf.width ?? 0),
        0
      );
      const lastColWidth = Math.max(5, data.width - totalOtherWidth);
      const nextDsSubfields = [
        ...allButLast,
        { ...dsSubfields[dsSubfields.length - 1], width: lastColWidth } as DatasheetFieldComponentGetDto,
      ];

      onResize(id, {
        ...datasheetElement,
        ...data,
        original: {
          ...datasheetElement.original,
          dssubfields: nextDsSubfields,
          m_ds_subfields: nextDsSubfields,
        },
      });
    },
    [onResize, datasheetElement]
  );

  const datasheetElementController = useDatasheetElementController({
    isTabOrderSettingActive,
    resizeHandlerStrategy,
    datasheetElement,
    containerRef: containerRef,
    // Use the shared minimum box size (26x26px) for table fields as well.
    minWidth: DATASHEET_MIN_FIELD_SIZE,
    minHeight: DATASHEET_MIN_FIELD_SIZE,
    isSelected,
    onSelect,
    onCoordinatesChange,
    onResize: handleOuterResize,
    datasheetGridSettings,
    multipleElementsSelected,
  });

  const headingReference = React.useRef<HTMLDivElement>(null);
  const hydratedSubfieldsRef = React.useRef(false);
  const [isHydrated, setIsHydrated] = React.useState(
    !shouldHydrateTableSubfields(datasheetElement.original)
  );

  const mapFieldToOriginDto = React.useCallback((fieldDto: FieldPrimaryListDto): DatasheetFieldComponentOriginFieldGetDto => {
    const getFieldTypeName = (type: FieldType): string => {
      const typeNames: Record<FieldType, string> = {
        [FieldType.system]: 'System',
        [FieldType.text]: 'Text',
        [FieldType.date]: 'Date',
        [FieldType.number]: 'Number',
        [FieldType.checkbox]: 'Yes/No',
        [FieldType.dropdown]: 'Dropdown',
        [FieldType.image]: 'Image',
        [FieldType.lookupTable]: 'Lookup Table',
        [FieldType.table]: 'Table',
        [FieldType.computed]: 'Computed',
        [FieldType.summary]: 'Summary',
        [FieldType.tableSummary]: 'Table Summary',
        [FieldType.hyperlink]: 'Hyperlink',
        [FieldType.integer]: 'Integer',
      };
      return typeNames[type] || 'Unknown';
    };

    return {
      id: fieldDto.id,
      fieldid: fieldDto.id,
      fieldname: fieldDto.name,
      fieldtype: {
        typeid: fieldDto.type,
        typename: getFieldTypeName(fieldDto.type),
        imageURL: fieldDto.imageURL,
        fieldClass: '',
        computed: fieldDto.type === FieldType.computed,
        systemType: fieldDto.system,
      },
      permissions: '',
      tableIndex: 0,
      defaultWidth: 0,
      imageURL: fieldDto.imageURL,
      fieldClass: '',
      calcField: false,
      computed: fieldDto.type === FieldType.computed,
      rawFieldType: fieldDto.type,
      table: fieldDto.type === FieldType.table,
      type: fieldDto.type,
      systemField: fieldDto.system,
      system: fieldDto.system,
      subfields: undefined,
      lookupTableInfo: undefined,
      fieldnameWithPath: fieldDto.fullPath || fieldDto.name,
      required: false,
      updateColsCount: 0,
      showEditorAllowed: true,
      showUploadAllowed: false,
      remoteFieldName: undefined,
      dataSourceNodeId: 0,
      remoteTableName: '',
      remoteTableKeyField: '',
      localMatchForRemoteKeyField: undefined,
      updateFieldname: fieldDto.name,
      autoEditOnClick: false,
      systemDateField: false,
      remoteTableRowIdentifierFieldname: '',
      fieldcontext: String(fieldDto.context),
      tableSubField: true,
      remoteField: false,
      confirmEntry: false,
      hyperlinkfield: fieldDto.type === FieldType.hyperlink,
      imageField: fieldDto.type === FieldType.image,
    };
  }, []);

  React.useEffect(() => {
    const originField = datasheetElement.original.field;
    const fieldId = originField.fieldid ?? originField.id;

    if (hydratedSubfieldsRef.current) {
      return;
    }

    if (!shouldHydrateTableSubfields(datasheetElement.original)) {
      hydratedSubfieldsRef.current = true;
      setIsHydrated(true);
      return;
    }

    if (!fieldId) {
      return;
    }

    const contextValue = (() => {
      const raw = (originField as any).context ?? originField.fieldcontext;
      if (typeof raw === 'number') return raw;
      if (typeof raw === 'string') {
        const parsed = parseInt(raw, 10);
        return Number.isNaN(parsed) ? 1 : parsed;
      }
      return 1;
    })();

    const tabId = (datasheetElement.original as { tabid?: number; tabId?: number }).tabid
      ?? (datasheetElement.original as { tabid?: number; tabId?: number }).tabId
      ?? 0;

    let cancelled = false;

    const subfieldsPromise = getTableFieldSubfields({ fieldId, context: contextValue });
    const reportFormatsPromise =
      parentFormatId && parentFormatId > 0 && tabId > 0
        ? getTableReportFormats({ context: contextValue, dsFormatId: parentFormatId, tabId, tableFieldId: fieldId })
        : Promise.resolve([]);

    Promise.all([subfieldsPromise, reportFormatsPromise])
      .then(([subfields, formats]) => {
        if (cancelled) return;

        if (!Array.isArray(subfields) || subfields.length === 0) {
          hydratedSubfieldsRef.current = true;
          setIsHydrated(true);
          return;
        }

        const mappedSubfields = subfields.map((subfield) => mapFieldToOriginDto(subfield));
        hydratedSubfieldsRef.current = true;

        const hasExplicitDsSubfields = Object.prototype.hasOwnProperty.call(datasheetElement.original, "dssubfields");
        const hasConfiguredDsSubfields = hasExplicitDsSubfields && Array.isArray(datasheetElement.original.dssubfields);
        const hasLegacyDsSubfields = (datasheetElement.original.m_ds_subfields?.length ?? 0) > 0;
        const hasExistingDsSubfields = hasConfiguredDsSubfields || hasLegacyDsSubfields;

        if (hasExistingDsSubfields) {
          onResize(datasheetElement.id, {
            ...datasheetElement,
            original: {
              ...datasheetElement.original,
              field: {
                ...originField,
                subfields: mappedSubfields,
              },
            },
          });
          setIsHydrated(true);
          return;
        }

        const defaultFormat = formats.find((format) => format.isDefault);
        if (defaultFormat && Array.isArray(defaultFormat.rows) && defaultFormat.rows.length > 0) {
          const visibleRows = defaultFormat.rows
            .filter((row) => Number(row.order) > 0)
            .sort((a, b) => Number(a.order) - Number(b.order));

          const subfieldMap = new Map(mappedSubfields.map((sf) => [sf.fieldid, sf]));
          const orderedDsSubfields = visibleRows
            .map((row) => {
              const field = subfieldMap.get(row.subfield.fieldId);
              if (!field) return null;
              return {
                id: field.fieldid,
                field,
                width: row.width ?? field.defaultWidth ?? 100,
                height: 0,
                x: 0,
                y: 0,
              } as unknown as DatasheetFieldComponentGetDto;
            })
            .filter(Boolean) as DatasheetFieldComponentGetDto[];

          onResize(datasheetElement.id, {
            ...datasheetElement,
            original: {
              ...datasheetElement.original,
              field: {
                ...originField,
                subfields: mappedSubfields,
              },
              dssubfields: orderedDsSubfields,
              m_ds_subfields: orderedDsSubfields,
              tableReportFormat: defaultFormat.reportName,
            },
          });
          setIsHydrated(true);
          return;
        }

        const initialDsSubfields = mappedSubfields.map((subfield) => ({
          id: subfield.fieldid,
          field: subfield,
          width: datasheetElement.width,
          height: 0,
          x: 0,
          y: 0,
        } as unknown as DatasheetFieldComponentGetDto));

        onResize(datasheetElement.id, {
          ...datasheetElement,
          original: {
            ...datasheetElement.original,
            field: {
              ...originField,
              subfields: mappedSubfields,
            },
            dssubfields: initialDsSubfields,
            m_ds_subfields: initialDsSubfields,
          },
        });
        setIsHydrated(true);
      })
      .catch(() => {
        if (!cancelled) {
          hydratedSubfieldsRef.current = true;
          setIsHydrated(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [datasheetElement, onResize, mapFieldToOriginDto, parentFormatId]);

  React.useEffect(() => {
    datasheetElementController.handleHeadingSizeChange({
      width: headingReference.current?.offsetWidth || 0,
      height: headingReference.current?.offsetHeight || 0,
    });
  }, [
    datasheetElement.original.headtext,
    datasheetElement.original.headfontsize,
    datasheetElement.original.headfont,
    datasheetElement.original.headdisplay,
    datasheetElement.original.headpos,
  ]);

  const handleResizeColumn = React.useCallback(
    ({ field }: { field: DatasheetFieldComponentOriginFieldGetDto }) => {
      const existingDsSubfields =
        datasheetElement.original.dssubfields ||
        datasheetElement.original.m_ds_subfields ||
        [];

      const nextDsSubfields = (existingDsSubfields.length > 0
        ? existingDsSubfields
        : (datasheetElement.original.field.subfields || []).map((subField) => ({
            id: subField.fieldid,
            field: subField,
            width: subField.defaultWidth || 100,
            height: 0,
            x: 0,
            y: 0,
          })) as Array<DatasheetFieldComponentGetDto>
      ).map((subField) =>
        subField.field?.fieldid === field.fieldid
          ? {
              ...subField,
              width: (field as any).width ?? subField.width,
              field,
            }
          : subField
      );

      onResize(datasheetElement.id, {
        ...datasheetElement,
        original: {
          ...datasheetElement.original,
          dssubfields: nextDsSubfields,
          m_ds_subfields: nextDsSubfields,
          field: {
            ...datasheetElement.original.field,
          },
        },
      });
    },
    [onResize, datasheetElement]
  );

  const headingPosition = datasheetElement.original.headpos;
  const headingPlacedHorizontally = [
    DatasheetFieldComponentHeadingPosition.left,
    DatasheetFieldComponentHeadingPosition.right,
  ].includes(headingPosition);

  const handleTabOrderChange = React.useCallback(() => {
    onTabOrderChange(datasheetElement);
  }, [onTabOrderChange, datasheetElement]);

  return {
    headingReference,
    headingPlacedHorizontally,
    headingPosition,
    handleResizeColumn,
    handleTabOrderChange,
    isHydrated,
    ...datasheetElementController,
  };
};
